import { fp32ArrayToFp16Bytes } from './fp16';
import { patchCenterToOriginal } from './unletterboxBlob';
import type { UnletterboxContext } from './unletterboxBlob';

export type RasterizeHeatmapArgs = {
  heatmap: Float32Array; // gridH*gridW, row-major
  gridH: number;
  gridW: number;
  ctx: UnletterboxContext;
};

export type DenseHeatmap = {
  data: Float32Array; // width*height, row-major, cropped-image pixel space
  width: number;
  height: number;
};

// Rasterize the patch-grid heatmap to a dense single-channel map at the
// cropped-image (originalW x originalH) resolution that the boxes/image share.
// `patchCenterToOriginal(row, col, ctx)` is affine in (row, col), so we recover
// the grid->pixel transform from three patch centers, invert it, and bilinearly
// sample the grid at each output pixel.
export function rasterizeHeatmap(args: RasterizeHeatmapArgs): DenseHeatmap {
  const { heatmap, gridH, gridW, ctx } = args;
  const width = ctx.originalW;
  const height = ctx.originalH;

  // Affine grid->pixel: p = p00 + col*dCol + row*dRow.
  const p00 = patchCenterToOriginal(0, 0, ctx);
  const p01 = patchCenterToOriginal(0, 1, ctx);
  const p10 = patchCenterToOriginal(1, 0, ctx);
  const dColX = p01.x - p00.x;
  const dColY = p01.y - p00.y;
  const dRowX = p10.x - p00.x;
  const dRowY = p10.y - p00.y;

  // Invert the 2x2 [[dColX, dRowX],[dColY, dRowY]] to map pixel->(col,row).
  const det = dColX * dRowY - dRowX * dColY || 1e-8;
  const iColX = dRowY / det;
  const iColY = -dRowX / det;
  const iRowX = -dColY / det;
  const iRowY = dColX / det;

  const sample = (row: number, col: number): number => {
    const r = Math.min(Math.max(row, 0), gridH - 1);
    const c = Math.min(Math.max(col, 0), gridW - 1);
    const r0 = Math.floor(r);
    const c0 = Math.floor(c);
    const r1 = Math.min(r0 + 1, gridH - 1);
    const c1 = Math.min(c0 + 1, gridW - 1);
    const fr = r - r0;
    const fc = c - c0;
    const v00 = heatmap[r0 * gridW + c0];
    const v01 = heatmap[r0 * gridW + c1];
    const v10 = heatmap[r1 * gridW + c0];
    const v11 = heatmap[r1 * gridW + c1];
    const top = v00 * (1 - fc) + v01 * fc;
    const bot = v10 * (1 - fc) + v11 * fc;

    return top * (1 - fr) + bot * fr;
  };

  const data = new Float32Array(width * height);

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const px = x - p00.x;
      const py = y - p00.y;
      const col = px * iColX + py * iColY;
      const row = px * iRowX + py * iRowY;
      data[y * width + x] = sample(row, col);
    }
  }

  return { data, width, height };
}

export type DenseHeatmapFp16 = {
  data: Uint8Array; // width*height fp16 little-endian bytes
  width: number;
  height: number;
};

// Rasterize and fp16-pack in one step — the shape `SegmentMask` callers send
// over the wire (`heatmapDense`). Shared by runLocate and the teach preview
// loops so the rasterize + encode pairing can't drift between call sites.
export function rasterizeHeatmapFp16(
  args: RasterizeHeatmapArgs,
): DenseHeatmapFp16 {
  const dense = rasterizeHeatmap(args);

  return {
    data: fp32ArrayToFp16Bytes(dense.data),
    width: dense.width,
    height: dense.height,
  };
}
